import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { updateConsent, CONSENT_KEY, CookiePreferences, trackEvent } from "@/lib/analytics";

interface CookieConsentProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CookieConsent = ({ isOpen: controlledOpen, onOpenChange }: CookieConsentProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    advertising: false,
  });

  // Determine if this is a controlled instance (from Footer)
  const isControlledInstance = controlledOpen !== undefined;

  // Use controlled state if provided, otherwise use internal state for preferences modal
  const isPreferencesOpen = controlledOpen !== undefined ? controlledOpen : showPreferences;
  const setIsPreferencesOpen = onOpenChange || setShowPreferences;

  useEffect(() => {
    // Check if user has already made a choice
    const consentString = localStorage.getItem(CONSENT_KEY);
    
    if (!consentString) {
      // Only show banner if this is NOT a controlled instance
      if (!isControlledInstance) {
        setShowBanner(true);
      }
    } else {
      try {
        const consent = JSON.parse(consentString) as CookiePreferences;
        setPreferences(consent);
        
        // Update GA consent based on stored preferences
        updateConsent(consent);
      } catch (e) {
        // Handle old consent format (string)
        const oldConsent = consentString === "accepted";
        const newPreferences = {
          necessary: true,
          analytics: oldConsent,
          advertising: oldConsent,
        };
        setPreferences(newPreferences);
        updateConsent(newPreferences);
      }
    }
  }, [isControlledInstance]);

  const handleAcceptAll = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    updateConsent(newPreferences);
    trackEvent("cookie_consent", { choice: "accept_all" });
    setShowBanner(false);
    setIsPreferencesOpen(false);
  };

  const handleRejectNonEssential = () => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      advertising: false,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(newPreferences));
    setPreferences(newPreferences);
    updateConsent(newPreferences);
    trackEvent("cookie_consent", { choice: "reject_non_essential" });
    setShowBanner(false);
    setIsPreferencesOpen(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
    updateConsent(preferences);
    trackEvent("cookie_consent", {
      choice: "custom",
      analytics: preferences.analytics,
      advertising: preferences.advertising,
    });
    setIsPreferencesOpen(false);
    setShowBanner(false);
  };

  const handlePreferencesOpenChange = (open: boolean) => {
    if (!open) {
      // Auto-save preferences when closing the modal
      handleSavePreferences();
    }
    setIsPreferencesOpen(open);
  };

  const openPreferences = () => {
    setShowBanner(false);
    setIsPreferencesOpen(true);
  };

  return (
    <>
      {/* Bottom Banner */}
      {showBanner && !isControlledInstance && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg">
          <div className="container mx-auto px-4 py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1 text-sm text-foreground">
                <p className="mb-2">
                  We use cookies on our site to enhance your user experience, provide personalized content, and analyze our traffic.{" "}
                  <Link to="/privacy-policy" className="text-primary underline hover:text-primary/80">
                    Cookie Policy
                  </Link>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  onClick={handleAcceptAll}
                  variant="accept"
                  size="sm"
                  className="whitespace-nowrap w-full sm:w-auto"
                >
                  Accept all
                </Button>
                <Button
                  onClick={handleRejectNonEssential}
                  variant="decline"
                  size="sm"
                  className="whitespace-nowrap w-full sm:w-auto"
                >
                  Reject non-essential
                </Button>
                <Button
                  onClick={openPreferences}
                  variant="tertiary"
                  size="sm"
                  className="whitespace-nowrap w-full sm:w-auto"
                >
                  Preferences &gt;
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      <Dialog open={isPreferencesOpen} onOpenChange={handlePreferencesOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Customize your cookie preferences</DialogTitle>
            <DialogDescription className="pt-2">
              We respect your right to privacy. You can choose not to allow some types of cookies. Your cookie preferences will apply across our website.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
              <div className="flex-1 space-y-1">
                <Label htmlFor="necessary" className="text-base font-semibold cursor-pointer">
                  Necessary
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies are necessary for the website to function properly and cannot be switched off. They help with things like logging in and setting your privacy preferences.
                </p>
              </div>
              <Switch
                id="necessary"
                checked={preferences.necessary}
                disabled
                className="mt-1"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
              <div className="flex-1 space-y-1">
                <Label htmlFor="analytics" className="text-base font-semibold cursor-pointer">
                  Analytics
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies help us improve the site by tracking which pages are most popular and how visitors move around the site.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, analytics: checked })
                }
                className="mt-1"
              />
            </div>

            {/* Advertising Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <Label htmlFor="advertising" className="text-base font-semibold cursor-pointer">
                  Advertising
                </Label>
                <p className="text-sm text-muted-foreground">
                  These cookies provide extra features and personalization to improve your experience. They may be set by us or by partners whose services we use.
                </p>
              </div>
              <Switch
                id="advertising"
                checked={preferences.advertising}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, advertising: checked })
                }
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button
              onClick={handleAcceptAll}
              variant="accept"
              className="flex-1"
            >
              Accept all
            </Button>
            <Button
              onClick={handleRejectNonEssential}
              variant="decline"
              className="flex-1"
            >
              Reject non-essential
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
